import React, { useRef, useEffect, useState, useCallback } from 'react';
import './cameraCapture.css';

// In-app camera. Streams the device camera into a <video>, captures the current frame
// to a JPEG File, and hands it back via onCapture(file). Works on desktop webcams and
// mobile (rear camera by default, with a flip toggle). Requires a secure context
// (https or localhost) — getUserMedia is blocked on plain http.
const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState('environment'); // rear camera first
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async (mode) => {
    stop();
    setReady(false);
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (err) {
      console.error('Camera error:', err);
      setError(
        err?.name === 'NotAllowedError' || err?.name === 'SecurityError'
          ? 'Camera access was blocked. Allow camera permission (and use https) and try again.'
          : 'Could not access a camera on this device.'
      );
    }
  }, [stop]);

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser does not support in-app camera capture.');
      return undefined;
    }
    start(facingMode);
    return stop; // stop the stream on unmount / before re-starting for a flip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !ready) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        stop();
        onCapture(file);
      },
      'image/jpeg',
      0.9
    );
  };

  const close = () => {
    stop();
    onClose();
  };

  return (
    <div className="camera-overlay" onClick={close}>
      <div className="camera-modal" onClick={(e) => e.stopPropagation()}>
        {error ? (
          <div className="camera-error">
            <i className="fas fa-video-slash"></i>
            <p>{error}</p>
            <button className="camera-btn camera-text-btn" onClick={close}>Close</button>
          </div>
        ) : (
          <>
            <video ref={videoRef} className="camera-video" playsInline muted autoPlay />
            <div className="camera-controls">
              <button className="camera-btn camera-cancel" onClick={close} title="Cancel">
                <i className="fas fa-times"></i>
              </button>
              <button
                className="camera-shutter"
                onClick={capture}
                disabled={!ready}
                title="Take photo"
                aria-label="Take photo"
              />
              <button
                className="camera-btn camera-flip"
                onClick={() => setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'))}
                title="Switch camera"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CameraCapture;
